<template>
  <div class="sor-banner">
    <div class="sor-eyebrow">System of record</div>
    <div class="sor-row">
      <div class="sor-text">
        <div class="sor-title">Which CRM owns record ownership</div>
        <div class="sor-desc">
          Drives who sends outreach on behalf of each prospect. Other connected CRMs stay
          available as data sources.
        </div>
      </div>
      <div class="sor-control">
        <div class="sor-select">
          <SearchSelect
            :model-value="crmOfRecord"
            :options="options"
            placeholder="No CRM selected"
            allow-clear
            clear-label="None"
            hide-search
            @update:model-value="onSelect"
          />
        </div>
        <AutosaveBadge :status="status" :error-message="errorMessage" @retry="retry" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import SearchSelect from '../../../components/SearchSelect.vue'
import AutosaveBadge from './AutosaveBadge.vue'
import { apiFetch } from '../../../services/api'
import { useAutosave } from '../../composables/useAutosave'
import { logIntegrationFetchError } from './log'

const emit = defineEmits<{ (e: 'changed', crm: string): void }>()

const options = [
  { value: 'salesforce', label: 'Salesforce', domain: 'salesforce.com' },
  { value: 'hubspot', label: 'HubSpot', domain: 'hubspot.com' },
  { value: 'pipedrive', label: 'Pipedrive', domain: 'pipedrive.com' },
]

const crmOfRecord = ref('')
const ownerConfig = ref<Record<string, unknown> | null>(null)

const { status, errorMessage, saveNow, retry } = useAutosave(persist)

async function load() {
  try {
    const resp = await apiFetch('/settings/ownership_settings')
    crmOfRecord.value = resp?.crm_of_record || ''
    ownerConfig.value = resp?.default_owner_config || null
  } catch (err) {
    logIntegrationFetchError('System of Record', 'load ownership settings', err)
  }
}

function onSelect(value: string) {
  crmOfRecord.value = value || ''
  void saveNow()
}

async function persist() {
  const value = crmOfRecord.value
  // Fresh read-modify-write: re-GET so we MERGE into the latest owner config and never drop another
  // CRM's key. The banner owns crm_of_record; per-CRM owner fields are seeded here for HubSpot/Pipedrive
  // (fixed standard field) but Salesforce's are managed in Field mappings — so we only seed a missing key.
  let current: Record<string, unknown>
  try {
    const resp = await apiFetch('/settings/ownership_settings')
    current = (resp?.default_owner_config && typeof resp.default_owner_config === 'object')
      ? { ...resp.default_owner_config } : {}
  } catch (err) {
    logIntegrationFetchError('System of Record', 'reload ownership settings before save', err)
    current = ownerConfig.value ? { ...ownerConfig.value } : {}
  }
  if (value === 'hubspot' && !current.hubspot) current.hubspot = { account: 'hubspot_owner_id', contact: 'hubspot_owner_id' }
  if (value === 'pipedrive' && !current.pipedrive) current.pipedrive = { account: 'owner_id', contact: 'owner_id' }
  const default_owner_config = Object.keys(current).length ? current : null
  await apiFetch('/settings/ownership_settings', {
    method: 'PUT',
    body: { crm_of_record: value || null, default_owner_config },
  })
  ownerConfig.value = default_owner_config
  emit('changed', value)
}

onMounted(load)
defineExpose({ reload: load })
</script>

<style scoped>
.sor-banner {
  border: 1px solid var(--border);
  border-radius: 16px;
  background: var(--surface);
  padding: 16px 18px;
  margin-bottom: 24px;
}
.sor-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 8px;
}
.sor-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.sor-title {
  font-weight: 700;
  font-size: 15px;
  color: var(--text);
}
.sor-desc {
  margin-top: 4px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.4;
  max-width: 520px;
}
.sor-control {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sor-select {
  min-width: 200px;
}
</style>
